/* global $ */
import React, { useState, useEffect } from "react";
import DataTable from 'react-data-table-component';
import axios from "axios";
import Swal from 'sweetalert2';

const API_BASE = 'https://api.sivammoto.in/api';

const emptyRecord = {
    date: "",
    reg_no: "",
    year: "",
    model: "",
    showroom: "",
    person: "",
    phone: "",
    price: "",
    dealer_details: "",
};

const emptyFiles = {
    aadhar_file: null,
    rc_file: null,
};

// The API builds file URLs with url_for, which can come back as http:// behind the proxy.
// Browsers block http content on an https page, so upgrade the scheme to match the page.
const fixFileUrl = (url) => {
    if (!url) return "";
    return window.location.protocol === 'https:' ? url.replace(/^http:\/\//i, 'https://') : url;
};

const isPdfFile = (file) => /\.pdf$/i.test(file?.name || (file?.url || "").split('?')[0]);

function Dealer() {
    const [registrationno, setRegistrationno] = useState('');
    const [searchedRegNo, setSearchedRegNo] = useState('');
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [newRecord, setNewRecord] = useState(emptyRecord);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingFiles, setEditingFiles] = useState({});
    const [formKey, setFormKey] = useState(0);
    const [selectedRow, setSelectedRow] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    const [files, setFiles] = useState(emptyFiles);

    const [errors, setErrors] = useState({});

    const allowedFormats = ["application/pdf", "image/jpeg", "image/png"];

    const resetForm = () => {
        setNewRecord(emptyRecord);
        setFiles(emptyFiles);
        setErrors({});
        setIsEditMode(false);
        setEditingId(null);
        setEditingFiles({});
        // Remount the form so the file inputs are cleared
        setFormKey(prev => prev + 1);
    };

    // Clear the form when the add/edit modal is closed
    useEffect(() => {
        $('#addRowModal').on('hidden.bs.modal', resetForm);
        return () => {
            $('#addRowModal').off('hidden.bs.modal', resetForm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeModal = (selector) => {
        $(selector).modal('hide');
        // Ensure backdrop is removed correctly by Bootstrap
        setTimeout(() => {
            $('body').removeClass('modal-open');
            $('.modal-backdrop').remove();
            $('body').css('padding-right', '');
            $('body').css('overflow', '');
        }, 500);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setNewRecord(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddButtonClick = () => {
        resetForm();
        $('#addRowModal').modal('show');
    };

    const handleEditButtonClick = (row) => {
        resetForm();
        setIsEditMode(true);
        setEditingId(row.dealer_id);
        setEditingFiles(row.files || {});
        setNewRecord({
            date: row.dealer_datas?.date || "",
            reg_no: row.reg_no || "",
            year: row.dealer_datas?.year || "",
            model: row.dealer_datas?.model || "",
            showroom: row.dealer_datas?.showroom || "",
            person: row.dealer_datas?.person || "",
            phone: row.dealer_datas?.phone || "",
            price: row.dealer_datas?.price ?? "",
            dealer_details: row.dealer_datas?.dealer_detail || "",
        });
        $('#addRowModal').modal('show');
    };

    const handleViewButtonClick = (row) => {
        setSelectedRow(row);
        $('#viewRowModal').modal('show');
    };

    const handleDeleteButtonClick = async (row) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete the Dealer record for ${row.reg_no}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const modified_by = localStorage.getItem('user_id');
                await axios.delete(`${API_BASE}/dealer/delete/${row.dealer_id}`, {
                    data: { modified_by: modified_by }
                });

                await Swal.fire('Deleted!', 'The Dealer record has been deleted.', 'success');

                fetchDealers(searchedRegNo, true);
            } catch (error) {
                console.error('Error deleting record:', error);
                const errorMessage = error.response?.data?.message || 'There was an issue deleting the record.';
                Swal.fire('Error!', errorMessage, 'error');
            }
        }
    };

    const handleFileChange = (e) => {
        const { id, files } = e.target;
        const file = files[0];

        if (file) {
            const { type, size } = file;

            // Validate file format
            if (!allowedFormats.includes(type)) {
                setErrors((prev) => ({
                    ...prev,
                    [id]: "Invalid file type! Only PDF, JPG, and PNG allowed.",
                }));
                setFiles((prev) => ({ ...prev, [id]: null }));
                return;
            }

            // Validate file size
            if (
                (type === "application/pdf" && size > 100 * 1024) ||
                ((type === "image/jpeg" || type === "image/png") && size > 50 * 1024)
            ) {
                setErrors((prev) => ({
                    ...prev,
                    [id]: `File too large! Max size: ${type === "application/pdf" ? "100KB (PDF)" : "50KB (Image)"
                        }`,
                }));
                setFiles((prev) => ({ ...prev, [id]: null }));
                return;
            }

            // Clear errors and set file
            setErrors((prev) => ({ ...prev, [id]: "" }));
            setFiles((prev) => ({ ...prev, [id]: file }));
        }
    };

    // Validation function
    const validateForm = () => {
        let formErrors = {}; // Store validation errors

        if (!newRecord.reg_no) {
            formErrors.reg_no = "Registration Number is required";
        }

        if (!newRecord.price) {
            formErrors.price = "Price is required";
        }

        if (!newRecord.date) {
            formErrors.date = "Date is required";
        }

        // Files are only mandatory when adding; on edit the existing files are kept
        if (!isEditMode && !files.aadhar_file) {
            formErrors.aadhar_file = "Addhaar is required";
        }

        if (!isEditMode && !files.rc_file) {
            formErrors.rc_file = "Rc is required";
        }

        // If there are errors, set them in the state and return false
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return false;
        }

        return true;
    };

    const getErrorMessage = (error, fallback) =>
        error.response?.data?.message || error.response?.data?.error || fallback;

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setErrors({});
        const user_id = localStorage.getItem('user_id');
        const recordData = {
            date: newRecord.date,
            reg_no: newRecord.reg_no,
            year: newRecord.year,
            model: newRecord.model,
            showroom: newRecord.showroom,
            person: newRecord.person,
            phone: newRecord.phone,
            price: newRecord.price,
            dealer_details: newRecord.dealer_details,
        };

        let dealerId = editingId;

        // Step 1: Save the dealer record
        try {
            if (isEditMode) {
                await axios.put(`${API_BASE}/dealer/update/${editingId}`, { ...recordData, modified_by: user_id }, {
                    headers: { 'Content-Type': 'application/json' }
                });
            } else {
                const response = await axios.post(`${API_BASE}/dealer/add`, { ...recordData, created_by: user_id }, {
                    headers: { 'Content-Type': 'application/json' }
                });
                dealerId = response.data.dealer_id;
            }
        } catch (error) {
            console.error('Error saving record:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: getErrorMessage(error, 'There was an issue in saving the record.'),
            });
            return;
        }

        // Step 2: Save the files against the dealer_id
        try {
            if (isEditMode) {
                // Only send the files the user replaced
                if (files.aadhar_file || files.rc_file) {
                    const formDataWithFile = new FormData();
                    formDataWithFile.append("dealer_id", dealerId);
                    formDataWithFile.append("modified_by", user_id || 1);
                    if (files.rc_file) formDataWithFile.append("rc_file", files.rc_file);
                    if (files.aadhar_file) formDataWithFile.append("aadhar_file", files.aadhar_file);

                    await axios.post(`${API_BASE}/dealerfile/update`, formDataWithFile,
                        { headers: { "Content-Type": "multipart/form-data" } }
                    );
                }
                Swal.fire('Updated!', 'The Dealer record has been updated.', 'success');
            } else {
                const formDataWithFile = new FormData();
                formDataWithFile.append("dealer_id", dealerId);
                formDataWithFile.append("created_by", user_id || 1);
                formDataWithFile.append("rc_file", files.rc_file);
                formDataWithFile.append("aadhar_file", files.aadhar_file);

                await axios.post(`${API_BASE}/dealerfile/upload`, formDataWithFile,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                Swal.fire('Saved!', 'Form and file saved successfully', 'success');
            }
        } catch (error) {
            console.error('Error saving File:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error saving File!',
                text: getErrorMessage(error, 'There was an issue in saving the file.'),
            });
        }

        closeModal('#addRowModal');

        // Refresh the results so the change shows up immediately
        if (searchedRegNo) {
            fetchDealers(searchedRegNo, true);
        }
    };

    const handleClear = () => {
        setRegistrationno('');
        setSearchedRegNo('');
        setData([]);
    };

    // silent: don't show an error popup when nothing is found (e.g. after deleting the last record)
    const fetchDealers = async (regNo, silent = false) => {
        try {
            const response = await axios.get(`${API_BASE}/dealer/${encodeURIComponent(regNo)}`);
            setData(response.data);
        } catch (error) {
            setData([]);
            if (silent && error.response?.status === 404) return;
            console.error('Error fetching vehicle registraion number data', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.response?.statusText || 'An unknown error occurred';
            Swal.fire('Error!', `There was an issue in searching the registration records: ${errorMessage}`, 'error');
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();

        const regNo = registrationno.trim();
        if (!regNo) {
            Swal.fire('Error!', 'Please Give the Vehicle Registration Number to search Dealer', 'error');
            return;
        }

        setSearchedRegNo(regNo);
        fetchDealers(regNo);
    };

    const openFile = (row, field, label) => {
        const file = row.files?.[field];
        if (file?.url) {
            setPreviewFile({ ...file, label: `${label} - ${row.reg_no}` });
            $('#filePreviewModal').modal('show');
        } else {
            Swal.fire('Not available', `${label} file not available.`, 'info');
        }
    };

    const renderFilePreview = (file, label) => {
        if (!file?.url) {
            return <p className="text-muted mb-0">{label} file not available.</p>;
        }

        const url = fixFileUrl(file.url);
        return (
            <div>
                {isPdfFile(file) ? (
                    <iframe src={url} title={label} style={{ width: '100%', height: '70vh', border: 'none' }} />
                ) : (
                    <img
                        src={url}
                        alt={label}
                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                    />
                )}
                <div className="text-center mt-2">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-external-link-alt" style={{ marginRight: '4px' }}></i>
                        Open in new tab
                    </a>
                </div>
            </div>
        );
    };

    const formatDate = (value) => {
        if (!value) return "";
        return new Date(value).toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
    };

    const filteredData = data.filter(item => {
        const query = searchText.toLowerCase();
        const d = item.dealer_datas || {};
        return [item.reg_no, d.date, d.model, d.showroom, d.person, d.phone, d.dealer_detail]
            .some(value => String(value ?? "").toLowerCase().includes(query));
    });

    const columns = [
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>No</span>,
            selector: (row, index) => index + 1,
            sortable: true,
            width: '60px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}> Date</span>,
            selector: row => formatDate(row.dealer_datas?.date),
            sortable: true,
            width: '120px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Registration No</span>,
            selector: row => row.reg_no || "",
            sortable: true,
            width: '170px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Year</span>,
            selector: row => row.dealer_datas?.year || "",
            sortable: true,
            width: '120px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Model</span>,
            selector: row => row.dealer_datas?.model || "",
            sortable: true,
            width: '150px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Showroom</span>,
            selector: row => row.dealer_datas?.showroom || "",
            sortable: true,
            width: '180px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Person</span>,
            selector: row => row.dealer_datas?.person || "",
            sortable: true,
            width: '180px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Phone</span>,
            selector: row => row.dealer_datas?.phone || "",
            sortable: true,
            width: '180px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Price</span>,
            selector: row => row.dealer_datas?.price ?? "",
            sortable: true,
            width: '120px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Dealer Details</span>,
            selector: row => row.dealer_datas?.dealer_detail || "",
            sortable: true,
            width: '180px',
            style: {
                fontWeight: '800'
            }
        },
        {
            name: <span style={{ textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold' }}>Actions</span>,
            cell: row => (
                <div className="d-flex">
                    <button onClick={() => handleViewButtonClick(row)} className="btn btn-info btn-sm" title="View" style={{ marginRight: '6px' }}>
                        <i className="fa fa-eye"></i>
                    </button>
                    <button onClick={() => openFile(row, 'aadhar_file', 'Aadhar')} className="btn btn-secondary btn-sm" title="Aadhar" style={{ marginRight: '6px' }}>
                        <i className="fa fa-id-card"></i>
                    </button>
                    <button onClick={() => openFile(row, 'rc_file', 'RC Book')} className="btn btn-success btn-sm" title="RC Book" style={{ marginRight: '6px' }}>
                        <i className="fa fa-file-alt"></i>
                    </button>
                    <button onClick={() => handleEditButtonClick(row)} className="btn btn-warning btn-sm" title="Edit" style={{ marginRight: '6px' }}>
                        <i className="fa fa-edit"></i>
                    </button>
                    <button onClick={() => handleDeleteButtonClick(row)} className="btn btn-danger btn-sm" title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ),
            width: '230px',
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    const viewDetails = selectedRow ? [
        ['Date', formatDate(selectedRow.dealer_datas?.date)],
        ['Registration No', selectedRow.reg_no],
        ['Year', selectedRow.dealer_datas?.year],
        ['Model', selectedRow.dealer_datas?.model],
        ['Showroom', selectedRow.dealer_datas?.showroom],
        ['Person', selectedRow.dealer_datas?.person],
        ['Phone', selectedRow.dealer_datas?.phone],
        ['Price', selectedRow.dealer_datas?.price],
        ['Dealer Details', selectedRow.dealer_datas?.dealer_detail],
    ] : [];

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">Dealer Search</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item active">Dealer Search</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col d-flex justify-content-end">
                    <button
                        className="btn btn-primary mr-2"
                        onClick={handleAddButtonClick}
                    >
                        <i className="fa fa-plus" style={{ marginRight: '8px' }}></i>
                        Add Dealer
                    </button>
                </div>
            </div>

            <div className="row">
                <div className="col-md-8">
                    <form onSubmit={handleSearch}>
                        <div className="row">
                            <div className="col-md-12 offset-md-1">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="row align-items-center">
                                            <div className="col-md-6">
                                                <label>Vehicle Registration No:</label>
                                            </div>
                                            <div className="col-md-6">
                                                <input type="input" className="form-control" placeholder="Type a Registration No" value={registrationno} onChange={(e) => setRegistrationno(e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 d-flex align-items-center">
                                        <button type="submit" className="btn btn-primary mr-2">Search</button>
                                        <button
                                            className="btn btn-secondary"
                                            type="button"
                                            onClick={handleClear}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <div className="row mb-2 mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="form-control mb-3"
                            />
                            <DataTable
                                columns={columns}
                                data={filteredData}
                                pagination
                                paginationPerPage={100}  // Set number of rows per page
                                paginationRowsPerPageOptions={[100, 200, 300, 400]}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            <div className="modal fade" id="addRowModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">{isEditMode ? 'Edit Dealer' : 'Add Dealer'}</h4>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <form key={formKey} onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Date</label>
                                            <input
                                                type="date"
                                                value={newRecord.date || ''}
                                                onChange={(e) => setNewRecord(prev => ({ ...prev, date: e.target.value }))}
                                                className="form-control"
                                            />
                                            {errors.date && <small className="text-danger">{errors.date}</small>}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Registration No</label>
                                            <input type="text" name="reg_no" value={newRecord.reg_no} onChange={handleInputChange} className="form-control" />
                                            {errors.reg_no && <small className="text-danger">{errors.reg_no}</small>}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Year</label>
                                            <input type="text" name="year" value={newRecord.year} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Model</label>
                                            <input type="text" name="model" value={newRecord.model} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Showroom</label>
                                            <input type="text" name="showroom" value={newRecord.showroom} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Person</label>
                                            <input type="text" name="person" value={newRecord.person} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Phone</label>
                                            <input type="text" name="phone" value={newRecord.phone} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Price</label>
                                            <input type="text" name="price" value={newRecord.price} onChange={handleInputChange} className="form-control" />
                                            {errors.price && <small className="text-danger">{errors.price}</small>}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label>Dealer Details</label>
                                            <input type="text" name="dealer_details" value={newRecord.dealer_details} onChange={handleInputChange} className="form-control" />
                                        </div>
                                    </div>
                                </div>

                                {["aadhar_file", "rc_file"].map((field) => (
                                    <div key={field} className="form-group">
                                        <label htmlFor={field}>
                                            {field.replace("_file", "").toUpperCase()}
                                        </label>
                                        <div className="input-group">
                                            <div className="custom-file">
                                                <input
                                                    type="file"
                                                    className="custom-file-input"
                                                    id={field}
                                                    onChange={handleFileChange}
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                />
                                                <label className="custom-file-label">
                                                    {files[field] ? files[field].name : "Choose file"}
                                                </label>
                                            </div>
                                        </div>
                                        {isEditMode && editingFiles[field]?.url && (
                                            <small className="text-muted d-block">
                                                Current file:{' '}
                                                <a href={fixFileUrl(editingFiles[field].url)} target="_blank" rel="noopener noreferrer">
                                                    {editingFiles[field].name || 'View'}
                                                </a>
                                                {' '}(leave empty to keep it)
                                            </small>
                                        )}
                                        {errors[field] && <small className="text-danger">{errors[field]}</small>}
                                    </div>
                                ))}

                                <div className="modal-footer justify-content-between">
                                    <button type="submit" className="btn btn-primary">{isEditMode ? 'Update' : 'Save changes'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* View Modal */}
            <div className="modal fade" id="viewRowModal">
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Dealer Details</h4>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {selectedRow && (
                                <>
                                    <table className="table table-bordered table-sm">
                                        <tbody>
                                            {viewDetails.map(([label, value]) => (
                                                <tr key={label}>
                                                    <th style={{ width: '30%' }}>{label}</th>
                                                    <td>{value ?? ""}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <h5>Aadhar</h5>
                                            {renderFilePreview(selectedRow.files?.aadhar_file, 'Aadhar')}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <h5>RC Book</h5>
                                            {renderFilePreview(selectedRow.files?.rc_file, 'RC Book')}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* File Preview Modal */}
            <div className="modal fade" id="filePreviewModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">{previewFile?.label}</h4>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {previewFile && renderFilePreview(previewFile, previewFile.label)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Dealer
